import { CartModel } from '../models/CartModel';
import { CartView } from '../views/CartView';
import { FormField, FormFields } from '../models/FormField';

export class CartController {
  private model: CartModel;
  private view: CartView;
  private eventId: string;
  private sessionId: string;
  private static instance: CartController;
  private fields!: FormFields;
  private hasInitializedValidation: boolean = false;

  private constructor() {
    this.model = new CartModel();
    this.view = new CartView();
    this.eventId = document.querySelector('[data-event-id]')?.getAttribute('data-event-id') || '';
    this.sessionId =
      document.querySelector('[data-session-id]')?.getAttribute('data-session-id') || '';
    this.initialize();
    this.initializeValidation();
  }

  public static getInstance(): CartController {
    if (!CartController.instance) {
      CartController.instance = new CartController();
    }
    return CartController.instance;
  }

  private initialize(): void {
    if ((window as any).initialCartState) {
      this.restoreCartState((window as any).initialCartState);
    }
    this.initializeEventListeners();
    this.view.initializeToggle();
  }

  private initializeEventListeners(): void {
    document.querySelectorAll('.quantity-control').forEach(control => {
      const input = control.querySelector('input') as HTMLInputElement;
      const minusBtn = control.querySelector('.minus') as HTMLButtonElement;
      const plusBtn = control.querySelector('.plus') as HTMLButtonElement;

      if (minusBtn && plusBtn) {
        minusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value || '0');
          if (currentValue > 0) {
            this.handleQuantityChange(input, currentValue - 1);
          }
        });

        plusBtn.addEventListener('click', () => {
          const currentValue = parseInt(input.value || '0');
          const maxValue = parseInt(input.getAttribute('max') || '6');

          if (currentValue < maxValue) {
            this.handleQuantityChange(input, currentValue + 1);
          }
        });
      }
    });

    // Ajouter l'écouteur pour le bouton "Continuer"
    document.addEventListener('click', event => {
      const target = event.target as HTMLElement;
      if (target.matches('[data-continue-order]')) {
        event.preventDefault();
        this.submitCart();
      }
    });
  }

  public handleQuantityChange(input: HTMLInputElement, newValue: number): void {
    // Forcer la mise à jour de la valeur
    input.setAttribute('value', newValue.toString());

    const audienceId = input.dataset.audienceId || '';
    const price = parseFloat(input.dataset.price || '0');
    const audienceName = this.getAudienceName(audienceId);

    if (newValue === 0) {
      this.model.removeItem(audienceId);
    } else {
      this.model.addItem(audienceId, audienceName, price, newValue);
    }

    this.updateView();
  }

  private updateView(): void {
    this.view.updateCart(this.model.getItems(), this.model.getTotal());
  }

  private getAudienceName(audienceId: string): string {
    const audienceElement = document
      .querySelector(`input[data-audience-id="${audienceId}"]`)
      ?.closest('.evenement-audiences__item')
      ?.querySelector('.evenement-audiences__category');
    return audienceElement?.textContent?.trim() || '';
  }

  private async submitCart(): Promise<void> {
    this.view.showLoader();
    this.view.hideError();

    try {
      const formData = new FormData();
      formData.append('action', 'create_cart');
      formData.append('nonce', (window as any).cartConfig.nonce);
      formData.append('cart_data', JSON.stringify(this.model.getItems()));
      formData.append('event_id', this.eventId);
      formData.append('session_id', this.sessionId);

      const response = await fetch((window as any).cartConfig.ajax_url, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      });

      const data = await response.json();

      if (data.success) {
        window.location.href = window.location.pathname + data.data.redirect_url;
      } else {
        this.view.hideLoader();
        this.view.showError(data.data.message || 'Une erreur est survenue');
      }
    } catch (error) {
      this.view.hideLoader();
      this.view.showError(
        (error as Error).message || 'Une erreur est survenue lors de la création du panier'
      );
      console.error('Error submitting cart:', error);
    } finally {
      // this.view.hideLoader();
    }
  }

  private async payOrder(isReserved): Promise<void> {
    this.view.showLoader();
    const formData = new FormData();
    formData.append('action', 'save_form_data');
    formData.append('nonce', (window as any).cartConfig.nonce);
    formData.append(
      'form_data',
      JSON.stringify({
        firstname: this.fields.firstname.element.value,
        lastname: this.fields.lastname.element.value,
        email: this.fields.email.element.value,
        phone: this.fields.phone.element.value,
        address: this.fields.address.element.value,
        zipcode: this.fields.zipcode.element.value,
        city: this.fields.city.element.value,
        cgv: this.fields.cgv.element.checked,
      })
    );

    try {
      const saveResponse = await fetch((window as any).cartConfig.ajax_url, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      });

      const saveData = await saveResponse.json();
      if (!saveData.success) {
        this.view.hideLoader();
        throw new Error(saveData.data.message);
      }

      // Ensuite procéder au paiement
      const paymentData = new FormData();
      paymentData.append('action', 'pay_order');
      paymentData.append('isReserved', isReserved);
      paymentData.append('nonce', (window as any).cartConfig.nonce);

      const paymentResponse = await fetch((window as any).cartConfig.ajax_url, {
        method: 'POST',
        body: paymentData,
        credentials: 'same-origin',
      });

      const data = await paymentResponse.json();
      if (data.success) {
        window.location.href = data.data.redirect_url;
        // this.view.hideLoader();
      } else {
        throw new Error(data.data.message);
      }
    } catch (error) {
      console.error('Error during payment process:', error);
      this.view.hideLoader();
    }
  }

  private restoreCartState(cart: any): void {
    if (cart && cart.items) {
      cart.items.forEach(item => {
        const input = document.querySelector(
          `input[data-audience-id="${item.id}"]`
        ) as HTMLInputElement;
        if (input) {
          this.handleQuantityChange(input, item.quantity);
        }
      });
    }
  }

  private initializeValidation(): void {
    this.fields = {
      firstname: {
        element: document.querySelector('#firstname') as HTMLInputElement,
        isValid: () => this.validateRequired('firstname'),
      },
      lastname: {
        element: document.querySelector('#lastname') as HTMLInputElement,
        isValid: () => this.validateRequired('lastname'),
      },
      email: {
        element: document.querySelector('#email') as HTMLInputElement,
        isValid: () => this.validateEmail(),
      },
      phone: {
        element: document.querySelector('#phone') as HTMLInputElement,
        isValid: () => this.validatePhone(),
      },
      city: {
        element: document.querySelector('#city') as HTMLInputElement,
        isValid: () => this.validateRequired('city'),
      },
      zipcode: {
        element: document.querySelector('#zipcode') as HTMLInputElement,
        isValid: () => this.validateZipcode(),
      },
      address: {
        element: document.querySelector('#address') as HTMLInputElement,
        isValid: () => this.validateRequired('address'),
      },
      cgv: {
        element: document.querySelector('#cgv') as HTMLInputElement,
        isValid: () => this.validateCheckbox('cgv'),
      },
    };

    // Ajouter les écouteurs d'événements pour chaque champ
    Object.keys(this.fields).forEach(fieldName => {
      const field = this.fields[fieldName];
      if (field.element) {
        field.element.addEventListener('input', () => this.validateForm());
        field.element.addEventListener('change', () => this.validateForm());
      }
    });
  }

  private validateRequired(fieldName: string): boolean {
    return this.fields[fieldName].element.value.trim() !== '';
  }

  private validateEmail(): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(this.fields.email.element.value);
  }

  private validatePhone(): boolean {
    const phoneRegex = /^\+?[0-9\s.-]+$/;
    return phoneRegex.test(this.fields.phone.element.value);
  }

  private validateZipcode(): boolean {
    const zipcodeRegex = /^\d{5}$/;
    return zipcodeRegex.test(this.fields.zipcode.element.value);
  }

  private validateCheckbox(fieldName: string): boolean {
    return this.fields[fieldName].element.checked;
  }

  private validateForm(): void {
    const isFormValid = Object.keys(this.fields).every(fieldName =>
      this.fields[fieldName].isValid()
    );

    if (isFormValid) {
      this.view.enablePaymentButton();
      if (!this.hasInitializedValidation) {
        this.initializePaymentButton();
        this.hasInitializedValidation = true;
      }
    } else {
      this.view.disablePaymentButton();
    }
  }

  private initializePaymentButton(): void {
    const paymentButton = document.querySelector('[data-pay-order]');
    const reservedButton = document.querySelector('[data-reserved]');
    if (paymentButton) {
      paymentButton.addEventListener('click', e => {
        e.preventDefault();
        this.submitPayment();
      });
    }
    if (reservedButton) {
      reservedButton.addEventListener('click', e => {
        e.preventDefault();
        this.submitPayment(true);
      });
    }
  }

  private async submitPayment(isReserved = false): Promise<void> {
    this.view.showLoader();
    try {
      await this.payOrder(isReserved);
    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      // this.view.hideLoader();
    }
  }
}

// Initialisation unique
CartController.getInstance();
